if (!customElements.get('ip-cart-drawer')) {
  class IpCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isOpen         = false;
      this.sectionId      = this.dataset.sectionId;
      this._updateCtrl    = null;
      this._refreshCtrl   = null;
      this._pendingClose  = false;
      this._qtyTimer      = null;
    }

    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('.js-ip-close'))  { this.close(); return; }
        const qtyBtn = e.target.closest('.js-ip-qty');
        if (qtyBtn) { this._handleQty(qtyBtn); return; }
        const rmBtn = e.target.closest('.js-ip-remove');
        if (rmBtn)  { this._handleRemove(rmBtn); return; }
        const upBtn = e.target.closest('.js-ip-upsell-add');
        if (upBtn) { this._addUpsell(upBtn); return; }
      });

      // Debounce manual input changes
      this.addEventListener('change', (e) => {
        const input = e.target.closest('.ip-qty__input');
        if (!input) return;
        clearTimeout(this._qtyTimer);
        this._qtyTimer = setTimeout(() => {
          this._updateQty(parseInt(input.dataset.line, 10), Math.max(0, parseInt(input.value, 10)));
        }, 300);
      });

      this._hijack();
      this._updateShippingBar();

      document.addEventListener('cart:update', (e) => {
        if (e.detail?.data?.source === 'ip-cart-drawer') return;
        if (e.detail?.data?.source === 'product-form-component') {
          this.open();
        }
        this._refresh();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });
    }

    _hijack() {
      const self = this;

      const noop = () => {};

      const suppressNative = () => {
        const hd = document.querySelector('cart-drawer-component');
        if (!hd) return;
        hd.showDialog = noop;
        hd.open       = noop;
        const dlg = hd.querySelector('dialog');
        if (dlg) { dlg.showModal = noop; dlg.show = noop; }
        setTimeout(() => restoreNative(hd), 500);
      };

      const restoreNative = (hd) => {
        hd.showDialog = () => self.open();
        hd.open       = () => self.open();
        const dlg = hd.querySelector('dialog');
        if (dlg) { dlg.showModal = () => self.open(); dlg.show = () => self.open(); }
      };

      const doHijack = () => {
        const hd = document.querySelector('cart-drawer-component');
        if (!hd) return;
        restoreNative(hd);
        const dlg = hd.querySelector('dialog');
        if (dlg?.open) dlg.close();
      };

      doHijack();
      customElements.whenDefined('cart-drawer-component')
        .then(() => setTimeout(doHijack, 80))
        .catch(() => {});
      setTimeout(doHijack, 300);

      // Capture phase: run before Horizon's native handler
      document.addEventListener('cart:update', (e) => {
        if (e.detail?.data?.suppressOpen) suppressNative();
      }, true);

      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-testid="cart-drawer-trigger"]')
          || e.target.closest('cart-icon')?.closest('button')
          || e.target.closest('cart-icon')?.closest('a');
        if (trigger) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.open();
        }
      }, true);
    }

    open() {
      if (this.isOpen) return;
      const dlg = document.querySelector('cart-drawer-component dialog');
      if (dlg?.open) dlg.close();

      this._pendingClose = false;
      this.isOpen = true;
      this.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.documentElement.classList.add('ip-cart-open');
      requestAnimationFrame(() => this.querySelector('.ip-cart-drawer__close')?.focus());
    }

    close() {
      if (!this.isOpen) return;
      this._pendingClose = true;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.documentElement.classList.remove('ip-cart-open');
      document.querySelector('[data-testid="cart-drawer-trigger"], cart-icon button, cart-icon a')?.focus();
      setTimeout(() => {
        this.setAttribute('aria-hidden', 'true');
        this._pendingClose = false;
      }, 360);
    }

    _handleQty(btn) {
      const item  = btn.closest('.ip-cart-item');
      const input = item.querySelector('.ip-qty__input');
      const line  = parseInt(input.dataset.line, 10);
      let qty     = parseInt(input.value, 10);

      if (btn.dataset.action === 'minus') qty = Math.max(0, qty - 1);
      if (btn.dataset.action === 'plus')  qty++;
      input.value = qty;

      qty === 0 ? this._removeItemOptimistic(item, line) : this._updateQty(line, qty);
    }

    _handleRemove(btn) {
      const line = parseInt(btn.dataset.line, 10);
      this._removeItemOptimistic(btn.closest('.ip-cart-item'), line);
    }

    _removeItemOptimistic(item, line) {
      item.classList.add('ip-cart-item--removing');
      const silent = () => this._updateQty(line, 0, { silent: this._pendingClose || !this.isOpen });
      const timer = setTimeout(silent, 350);
      item.addEventListener('animationend', () => { clearTimeout(timer); silent(); }, { once: true });
    }

    async _updateQty(line, quantity, opts = {}) {
      const silent = opts.silent || false;
      if (this._updateCtrl) this._updateCtrl.abort();
      this._updateCtrl = new AbortController();
      if (!silent) this._setLoading(true);

      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          signal: this._updateCtrl.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: this.sectionId, sections_url: window.location.pathname }),
        });
        const cart = await res.json();
        if (cart.errors) { console.error('[ip-cart]', cart.errors); return; }
        if (cart.sections?.[this.sectionId]) {
          this._render(cart.sections[this.sectionId]);
        }
        this._dispatch(cart, silent);
      } catch (e) {
        if (e.name !== 'AbortError') console.error('[ip-cart]', e);
      } finally {
        this._updateCtrl = null;
        if (!silent) this._setLoading(false);
      }
    }

    async _refresh() {
      if (this._refreshCtrl) this._refreshCtrl.abort();
      this._refreshCtrl = new AbortController();

      try {
        const res = await fetch(
          `${window.location.pathname}?sections=${this.sectionId}`,
          { signal: this._refreshCtrl.signal }
        );
        const json = await res.json();
        if (json[this.sectionId]) this._render(json[this.sectionId]);
      } catch (e) {
        if (e.name !== 'AbortError') console.error('[ip-cart]', e);
      } finally {
        this._refreshCtrl = null;
      }
    }

    _render(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newPanel = tmp.querySelector('.ip-cart-drawer__panel');
      const cur      = this.querySelector('.ip-cart-drawer__panel');
      if (newPanel && cur) {
        cur.innerHTML = newPanel.innerHTML;
        this._updateShippingBar();
      }
    }

    // Currency-aware shipping bar — uses Shopify.currency.rate to convert
    // the EUR threshold into the active presentment currency before comparing
    _updateShippingBar() {
      const bar = this.querySelector('[data-ip-shipping]');
      if (!bar) return;

      const thresholdEurCents = parseInt(bar.dataset.threshold, 10);
      const cartTotalCents    = parseInt(bar.dataset.cartTotal, 10);
      if (!thresholdEurCents) return;

      const rate              = parseFloat(window.Shopify?.currency?.rate) || 1;
      const thresholdConverted = Math.round(thresholdEurCents * rate);
      const reached            = cartTotalCents >= thresholdConverted;
      const progress           = Math.min(100, Math.round((cartTotalCents / thresholdConverted) * 100));
      const remaining          = Math.max(0, thresholdConverted - cartTotalCents);

      bar.classList.toggle('ip-shipping--reached', reached);

      const textEl = bar.querySelector('[data-ip-shipping-text]');
      if (textEl) {
        if (reached) {
          textEl.innerHTML = '¡Felicidades! Tienes envío GRATIS';
        } else {
          const activeCurrency = window.Shopify?.currency?.active || 'EUR';
          const currencySymbols = {
            'EUR': '€', 'USD': '$', 'GBP': '£', 'PEN': 'S/.', 'MXN': '$',
            'COP': 'COP$', 'ARS': '$', 'CLP': '$', 'BRL': 'R$', 'JPY': '¥',
            'CNY': '¥', 'CHF': 'CHF ', 'CAD': 'CA$', 'AUD': 'A$'
          };
          const symbol = currencySymbols[activeCurrency] || (activeCurrency + ' ');
          const fmt = (cents) => {
            if (window.Shopify?.formatMoney) {
              const numberOnly = Shopify.formatMoney(cents).replace(/[^0-9.,]/g, '').trim();
              return symbol + numberOnly;
            }
            return symbol + (cents / 100).toFixed(2).replace('.', ',');
          };
          textEl.innerHTML = 'Te faltan <strong>' + fmt(remaining) + '</strong> para envío GRATIS';
        }
      }

      const barEl = bar.querySelector('[data-ip-shipping-bar]');
      if (barEl) {
        barEl.style.width = progress + '%';
        barEl.classList.toggle('ip-shipping__bar--full', reached);
      }
    }

    async _addUpsell(btn) {
      const id = parseInt(btn.dataset.variantId, 10);
      if (!id) return;
      btn.disabled = true; btn.textContent = '…';
      this._setLoading(true);
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: this.sectionId, sections_url: window.location.pathname }),
        });
        const data = await res.json();
        if (data.status || data.errors) { console.error('[ip-cart]', data); btn.disabled = false; btn.textContent = 'Añadir'; return; }
        if (data.sections?.[this.sectionId]) this._render(data.sections[this.sectionId]);
        const cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
        this._dispatch(cart, true);
      } catch (e) {
        console.error('[ip-cart]', e);
      } finally {
        this._setLoading(false);
      }
    }

    _dispatch(cart, silent = false) {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: this.sectionId,
          data: {
            source: 'ip-cart-drawer',
            itemCount: cart.item_count,
            suppressOpen: silent || !this.isOpen,
          },
        },
      }));
    }

    _setLoading(on) {
      this.querySelector('.ip-cart-drawer__content')?.classList.toggle('ip-cart-drawer__content--loading', on);
    }
  }

  customElements.define('ip-cart-drawer', IpCartDrawer);
}